import { ErrorResponseBody } from 'api-paths-cas';
import { FetchError } from './error';

export enum Method {
  Get = 'GET',
  Post = 'POST',
  Delete = 'DELETE',
  Put = 'PUT',
}
const RETRY_API_CALL_NUMBER = 3;
const RETRY_API_CALL_INIT_DELAY = 3000;
const RETRY_API_CALL_INCREMENT = 1.8;
const API_CALL_TIMEOUT = 60000;

export interface ApiParams<BodyType = unknown> {
  domain: string;
  uri: string;
  idToken?: string;
  tempToken?: string;
  qp?: URLSearchParams;
  method?: string;
  body?: BodyType;
  bodyText?: string;
  abortController?: AbortController | false;
  includeCredentials?: boolean;
  headers?: Record<string, string>;
  origin?: string;
  retry?: boolean;
  retryOptions?: {
    n?: number;
    delay?: number;
    increment?: number;
    timeout?: number;
  };
  onRetry?: (retryNumber: number, time: number) => void;
  onReconnected?: () => void;
  onFailed?: (e: Error) => void;
}

async function fetchWithTimeoutAndRetry(
  url: string,
  options: any,
  retry = false,
  n: number = RETRY_API_CALL_NUMBER,
  delay: number = RETRY_API_CALL_INIT_DELAY,
  increment: number = RETRY_API_CALL_INCREMENT,
  timeout: number = API_CALL_TIMEOUT,
): Promise<Response> {
  try {
    const noResponseDefault = options.noResponseDefault || {};
    return await fetchWithTimeout(url, options, timeout)
      .then(async (response: Response | typeof noResponseDefault) => {
        if (response instanceof Response) {
          if (
            options.onReconnected &&
            typeof options.onReconnected === 'function' &&
            n !== RETRY_API_CALL_NUMBER // only fire reconnect if attemted to retry
          ) {
            options.onReconnected();
          }
          return response;
        }
        throw new FetchError({ message: `Response invalid: ${response}`, status: 500, url, options });
      })
      .catch(error => {
        throw new FetchError({ message: error.message, status: 500, url, options });
      });
  } catch (e) {
    if (n <= 1 || !retry) {
      if (options.onFailed && typeof options.onFailed === 'function') {
        options.onFailed(e);
      }
      console.error(JSON.stringify(e));
      throw e;
    }
    return new Promise(function (resolve, reject) {
      if (options.onRetry && typeof options.onRetry === 'function') {
        options.onRetry(n, delay);
      }
      setTimeout(
        function () {
          return fetchWithTimeoutAndRetry(url, options, retry, n - 1, (delay *= increment), increment, timeout)
            .then(resolve)
            .catch(reject);
        },
        n === RETRY_API_CALL_NUMBER ? delay + 1000 : delay,
      );
    });
  }
}
export async function queryData<BodyType = unknown, ReturnType = unknown>(
  params: ApiParams<BodyType>,
): Promise<ReturnType | ErrorResponseBody> {
  const {
    domain,
    uri,
    idToken,
    qp,
    method = Method.Get,
    body,
    bodyText,
    abortController = false,
    includeCredentials = false,
    onReconnected,
    onRetry,
    onFailed,
    retryOptions,
    retry,
    headers = null,
  } = params;
  let { origin } = params;

  const path = uri.replace(/^\/|\/$/g, ''); // remove leading forward slash for URL if it exists

  const baseUrl = new URL(path, domain);

  if (qp) {
    const searchParams = new URLSearchParams(qp);
    baseUrl.search = searchParams.toString();
  }

  const url = baseUrl.toString();

  const signal = abortController ? abortController.signal : undefined;

  if (!origin) {
    try {
      origin = window.location.origin;
    } catch (error) {
      origin = '';
    }
  }
  const options: any = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Cas-Shield-Site': origin,
      ...headers,
    },
    body: body ? JSON.stringify(body) : bodyText,
    credentials: includeCredentials ? 'include' : undefined,
    signal,
  };
  if (onReconnected) options.onReconnected = onReconnected;
  if (onRetry) options.onRetry = onRetry;
  if (onFailed) options.onFailed = onFailed;
  if (idToken) options.headers.Authorization = idToken;

  // Add user-alias header if set in localStorage and not already provided explicitly
  const userAlias = typeof window !== 'undefined' ? localStorage.getItem('user-alias') : null;
  if (userAlias && !options.headers['x-user-alias']) {
    options.headers['x-user-alias'] = userAlias;
  }

  if (retryOptions?.n && !retry) {
    console.warn(
      '"Retry" is not enabled. n will be ignored if "retry" argument is not true. Make sure you have passed "retry true"',
    );
  }
  const resp = await fetchWithTimeoutAndRetry(
    url,
    options,
    !!retry,
    retryOptions?.n,
    retryOptions?.delay,
    retryOptions?.increment,
    retryOptions?.timeout,
  );
  const respClone = resp.clone();
  if (resp.ok) {
    const contentType = resp.headers?.get('content-type') ?? '';
    if (contentType.includes('text/plain')) {
      return resp.text() as ReturnType;
    }
    try {
      return (await resp.json()) as ReturnType;
    } catch (e) {
      // If parsing fails, it's not JSON
      return respClone.text() as ReturnType;
    }
  }
  if (resp.status >= 400 && resp.status < 500) {
    return (await resp.json()) as ErrorResponseBody;
  }

  let message;
  let responseBody;
  try {
    responseBody = await resp.json();
  } catch (error: unknown) {
    if (error instanceof Error) {
      responseBody = error.message;
    }
  }

  if (typeof message === 'string') {
    message = responseBody;
  } else {
    message = responseBody.error || responseBody.message || JSON.stringify(responseBody);
  }

  throw new FetchError({ message, status: resp.status, url, options });
}
async function fetchWithTimeout(url: string, options: any, timeout: number = API_CALL_TIMEOUT): Promise<Response> {
  const resp = await Promise.race([
    fetch(url, options),
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), timeout)),
  ]);
  return resp as Response;
}
